/**
 * SetPasswordModal — modal de onboarding para crear contraseña tras el
 * primer login (login con temp password → TOTP → este modal).
 *
 * Aparece UNA SOLA VEZ por usuario. Tras éxito, llama a `mark_password_set`
 * RPC que setea `public.usuarios.password_set=true` para que próximos
 * logins (email + password + TOTP) no muestren el modal.
 *
 * NO es cancelable — el user debe decidir antes de seguir.
 *
 * `tempPasswordMode`: cuando true (caso del flujo de alta con temp password,
 * mig 0071), aparece un segundo botón "Conservar esta contraseña" que solo
 * marca el flag sin tocar auth.users — la temp queda como permanente. Cuando
 * false (default / users legacy que entran sin password), solo el botón
 * primario "Guardar contraseña" está disponible.
 */
import { useState, type ReactNode } from 'react';
import { getSupabase } from '../client';

interface SetPasswordModalProps {
  /** Callback cuando el password se creó exitosamente o se confirmó la temp. */
  onSuccess: () => void;
  /**
   * Si true, ofrece "Conservar esta contraseña" como atajo (mantiene la temp
   * password recibida por correo). Default: false (flujo legacy sin password).
   */
  tempPasswordMode?: boolean;
}

const MIN_LEN = 10;

export function SetPasswordModal({ onSuccess, tempPasswordMode = false }: SetPasswordModalProps): ReactNode {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [keeping, setKeeping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function validate(): string | null {
    if (password.length < MIN_LEN) return `La contraseña debe tener al menos ${MIN_LEN} caracteres.`;
    if (password !== confirm) return 'Las contraseñas no coinciden.';
    if (!/[a-z]/.test(password)) return 'Debe contener al menos una letra minúscula.';
    if (!/[A-Z]/.test(password)) return 'Debe contener al menos una letra mayúscula.';
    if (!/[0-9]/.test(password)) return 'Debe contener al menos un dígito.';
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validationErr = validate();
    if (validationErr) {
      setError(validationErr);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const sb = getSupabase();
      const { error: updErr } = await sb.auth.updateUser({ password });
      if (updErr) throw updErr;
      // Sincronizar el flag server-side. Si el RPC falla NO bloqueamos al
      // user (ya tiene password en auth.users), pero el modal volverá a
      // aparecer en el próximo login — log para investigación.
      const { error: rpcErr } = await sb.rpc('mark_password_set');
      if (rpcErr) {
        console.error('[SetPasswordModal] mark_password_set fallo:', rpcErr.message);
      }
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error inesperado.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleKeepTemp() {
    setKeeping(true);
    setError(null);
    try {
      const sb = getSupabase();
      // Solo flipear el flag — la temp password ya está en auth.users desde
      // que el BFF la creó. No hay updateUser aquí: el user sigue usando la
      // misma contraseña que recibió por correo.
      const { error: rpcErr } = await sb.rpc('mark_password_set');
      if (rpcErr) throw rpcErr;
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error inesperado al conservar la contraseña.');
    } finally {
      setKeeping(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="set-password-title"
      style={overlayStyle}
    >
      <form onSubmit={handleSubmit} style={cardStyle}>
        <h2 id="set-password-title" style={{ margin: 0, fontSize: 20 }}>
          {tempPasswordMode ? 'Tu contraseña' : 'Establece tu contraseña'}
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, marginTop: 4 }}>
          {tempPasswordMode
            ? 'Entraste con la contraseña temporal del correo. Puedes conservarla como permanente o elegir una nueva ahora. En ambos casos seguirás usando tu app autenticadora (TOTP) como segundo factor.'
            : 'Esta es tu primera vez aquí. Crea una contraseña permanente para los siguientes inicios de sesión. Después seguirás usando tu app autenticadora (TOTP) como segundo factor.'}
        </p>
        <label style={labelStyle}>
          <span>{tempPasswordMode ? 'Nueva contraseña (opcional)' : 'Nueva contraseña'}</span>
          <input
            type="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(null); }}
            autoComplete="new-password"
            minLength={MIN_LEN}
            required={!tempPasswordMode}
            autoFocus
            style={inputStyle}
            placeholder={`mínimo ${MIN_LEN} caracteres, mayúscula, minúscula y dígito`}
          />
        </label>
        <label style={labelStyle}>
          <span>Confirma la contraseña</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => { setConfirm(e.target.value); setError(null); }}
            autoComplete="new-password"
            required={!tempPasswordMode}
            style={inputStyle}
          />
        </label>
        {error && (
          <div role="alert" style={{ color: '#e57373', fontSize: 13, marginTop: 4 }}>{error}</div>
        )}
        <button
          type="submit"
          disabled={submitting || keeping || (tempPasswordMode && password.length === 0)}
          style={primaryBtnStyle}
        >
          {submitting ? 'Guardando…' : 'Guardar contraseña nueva'}
        </button>
        {tempPasswordMode && (
          <button
            type="button"
            onClick={handleKeepTemp}
            disabled={submitting || keeping}
            style={secondaryBtnStyle}
          >
            {keeping ? 'Confirmando…' : 'Conservar la contraseña temporal'}
          </button>
        )}
      </form>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
  display: 'grid', placeItems: 'center', zIndex: 9999,
  padding: 16,
};
const cardStyle: React.CSSProperties = {
  background: 'var(--bg-deep, #0c1216)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 12,
  padding: 24,
  width: 'min(440px, 100%)',
  display: 'flex', flexDirection: 'column', gap: 12,
  fontFamily: 'Inter, system-ui, sans-serif',
  color: 'var(--text, rgba(255,255,255,0.92))',
};
const labelStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-dim)',
};
const inputStyle: React.CSSProperties = {
  height: 38, padding: '0 12px', borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.18)',
  background: 'rgba(255,255,255,0.04)', color: 'var(--text)',
  fontSize: 13,
};
const primaryBtnStyle: React.CSSProperties = {
  marginTop: 8, padding: '10px 16px',
  background: 'var(--btn-primary-bg, #b4c96f)', color: '#0c1216',
  border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer',
  fontSize: 14,
};
const secondaryBtnStyle: React.CSSProperties = {
  marginTop: 4, padding: '10px 16px',
  background: 'transparent', color: 'rgba(255,255,255,0.85)',
  border: '1px solid rgba(255,255,255,0.22)', borderRadius: 6,
  fontWeight: 500, cursor: 'pointer', fontSize: 13,
};
